/**
 * Article quick-share (Jared, 2026-10-16): the words and links behind the share row under each research article's
 * subcopy and the pop-up it opens. Every sentence a reader sees in that pop-up lives here, so one brand check covers it.
 *
 * - The friend gets the article itself with the sharer's code on the end (aelofilter.com/research/<slug>?ref=CODE), so
 *   they land on the study, under the "Your friend invited you!" banner, not on the homepage.
 * - Each article's own message, written from its lead finding, comes first; "Try another message" (Jared, 2026-10-18)
 *   then offers the share card's other messages for that article (shareContent.ts ARTICLES). Plain ASCII on purpose
 *   (straight quotes, a plain hyphen): one curly quote turns a text message into Unicode, which halves what fits in it.
 * - "Invite friends, get free gifts", never "free stuff" (Jared, 2026-10-16).
 * - One experiment, one variable: only the row's line differs between the two groups (ROW_LINES).
 *
 * Pure (no DOM, no imports), so the Node tests check every line and link. The apps, their links and their order come from
 * share card v3 (shareCard.ts, shareContent.ts), so both list the same apps the same way.
 */

export const SITE = 'https://aelofilter.com'
const CODE = /^[A-Z0-9]{8,16}$/
const SLUG = /^[a-z0-9]+(?:-[a-z0-9]+)*$/

/**
 * The one experiment (Jared, 2026-10-16): the row's line. Group a is the default ("I think that's the winner"); b is the
 * line under test. Both as he chose them that evening: "get", not "earn", and "up to $225", the total at 50 friends.
 */
export const ROW_LINES = {
  a: 'Share with your friends and get free gifts, only for the month of October',
  b: 'For October only: share this article with people you love and get up to $225 in free gifts',
} as const
export type RowVariant = keyof typeof ROW_LINES

/** One message per research article, from its own lead finding (fact-checked against the article; see tests). */
export const ARTICLE_MESSAGES: Readonly<Record<string, string>> = {
  // Reverse osmosis article (2026-10-17): Jared's own line, word for word (relayed by the push session).
  'reverse-osmosis-microplastics':
    "Do RO filters remove microplastics? The answer isn't as simple as you may think...",
  // Nihart 2025 (University of New Mexico): every brain sample held plastic, including all 12 from people who died
  // with dementia, and those 12 held the most. Jared's own line, word for word.
  'microplastics-brain-memory-dementia':
    "Can't believe this - 100% of these dementia patients' brains had microplastics in them!?",
  // Marfella 2024 (NEJM): plastic in the neck-artery plaque of 150 of 257 patients; adjusted hazard ratio 4.53 for heart
  // attack, stroke or death within about 3 years. Jared's own share-card line (owner-decisions.md, 2026-10-16), word for
  // word; he is fine with the 4.5 figure here (it was only ever kept out of Meta ads).
  'microplastics-in-arteries-study':
    'Surgeons found plastic in the neck arteries of 58% of their patients. The patients with plastic were 4.5 times more likely to have a heart attack or stroke, or die, within about 3 years. Read this:',
  // Wang 2024 (EBioMedicine, Shantou): plastic in 24 of 30 clots (80%); stroke patients whose clots held more plastic had
  // more severe strokes.
  'microplastics-blood-clots-stroke':
    "Can't believe this - doctors found microplastics in 80% of the blood clots they removed, and stroke patients whose clots held more plastic had worse strokes!?",
  // Zheng 2025 (Shenyang): 5,670 children aged 7 to 10; more plastic in urine went with worse working memory (the
  // article's headline says "in their bodies").
  'microplastics-children-memory-behavior':
    "Can't believe this - in a study of 5,670 kids, the ones with more microplastics in their bodies did worse on memory tests!?",
  // Zhang 2024 (eBioMedicine): 100 of 100 men tested at fertility clinics; more kinds of plastic went with a low sperm count.
  'microplastics-fertility-pregnancy-baby-bottles':
    "Can't believe this - 100 of 100 men tested at fertility clinics had microplastics in their semen, and the men with more kinds were more likely to have a low sperm count!?",
  // Zhao 2022 and 2024 (University of Louisville): male mice on plastic water gained extra weight; in 2024 mostly fat.
  'microplastics-weight-gain-mice-studies':
    "Can't believe this - mice that drank microplastics in their water gained extra weight, mostly fat. What about us!?",
  // Olson 2000 (150 drugs): animal tests flagged 71% of the harms later seen in people; with microplastics, what showed up
  // first in rats and mice is now showing up in people (the article's words; never "in us", which claims it for everyone).
  'do-animal-studies-matter-for-humans':
    "Did you know animal tests caught 71% of the drug harms later seen in people? And with microplastics, what showed up in rats and mice first is now showing up in people.",
}

/**
 * Every sentence of the pop-up. Values are plain text (the pop-up sets textContent, never HTML). Brand-checked by the
 * aelo-brand-voice agent twice on 2026-10-16 (its rewrites are applied). Jared's own words are kept as he wrote them: the
 * row lines, introNote, introAsk, haveAccount and haveAccountLink, signInAsk, welcomeTitle, welcomeLine, shareTitle,
 * sentTitle, sentLine, sentFriends, the ladder's titles, notes and values, and the button labels.
 */
export const COPY = {
  // 1. The ladder as pictures you swipe through (Jared, Oct 16: "I want to see the sexy referral ladder"), one line, then
  // the email box ("first create your account"). "Already have an account?" turns the same box into a sign-in.
  introTitle: 'Invite friends, get free gifts',
  introSwipe: 'Swipe to see every gift',
  // Jared, Oct 18 evening, word for word ("Write it just like that"): why we give gifts, then what they get.
  introNote: 'Here’s how much we want everyone to stop drinking plastic: during the month of October, we will give you free gifts just for sharing this article with a friend. The more friends you share it with, the more free gifts you’ll get.',
  // "No purchase needed" stays on every offer surface (owner-decisions.md, 2026-10-16).
  introFine: 'Gifts stack. No purchase needed. Invites count through October 31, 11:59 p.m. ET.',
  // Jared, Oct 17 (reviewing the live pop-up), word for word.
  introAsk: 'To get your personal invite link and free gifts, first create your account.',
  emailLabel: 'Your email',
  emailButton: 'Create my free account',
  emailHelper: 'Next, we email you a 6-digit code.',
  haveAccount: 'Already have an account?',
  haveAccountLink: 'Click here',
  // A member signing back in: confirmed members get a sign-in link (the server sends codes only to people still
  // confirming), so the button says "link", never "code".
  signInAsk: 'Welcome back. Enter the email you created your Aelo account with.',
  signInButton: 'Request my login link',
  signInHelper: 'We’ll email you a link to sign in.',
  newHere: 'New here?',
  newHereLink: 'Create my free account',
  // Someone who joined but never confirmed gets the confirmation email instead (a code and a button), so this names
  // neither: both emails have a button, and both open the member page.
  signInSent: ['We sent an email to ', '. Tap the button in it, and your invite link is ready.'],
  // An address the server doesn't know: the sign-in pages' line ends "Use the form above"; here the way in is below.
  signInUnknown: 'Not joined yet? Tap “Create my free account” below.',
  // 2. The code
  codeTitle: 'Check your email',
  codeLine: ['We sent a 6-digit code to ', '. Type it here, or tap the button in that email.'],
  codeLabel: 'Type the code here',
  confirm: 'Confirm',
  checking: 'Checking…',
  wrongCode: 'That code doesn’t match. Check the newest email from Aelo and try again.',
  oneTryLeft: '1 try left.',
  triesLeft: ' tries left.',
  expired: 'That code has expired. Tap “Send a new code”.',
  limited: 'Too many tries. Wait a few minutes, then try again.',
  failed: 'We couldn’t check that code. Check your connection and try again.',
  notThere: 'Not there? Check Promotions or Spam.',
  resendCode: 'Send a new code',
  resendLink: 'Send it again',
  sending: 'Sending…',
  resent: 'Sent. Check your inbox again.',
  differentEmail: 'Use a different email',
  appMissing: 'We couldn’t open the app. Open your email yourself, find the email from Aelo, then come back here.',
  // A member who already confirmed gets a sign-in link instead of a code
  backTitle: 'Welcome back!',
  backLine: ['You already have an account. We sent a sign-in link to ', '. Tap it, and your invite link is ready.'],
  // Only if the code emails are ever switched off: a new signup confirms with the email's button instead.
  linkLine: ['We sent a confirmation link to ', '. Tap it to get your personal invite link.'],
  // 3, 4 and 5 on one screen after the code: "Welcome in!", a quick summary of the ladder they already saw, then the
  // message with their invite link and every way to send it.
  welcomeTitle: 'Welcome in!',
  welcomeLine: 'Your account is ready. Rewards stack, so who will you invite first?',
  // A member already signed in starts here instead.
  shareTitle: 'Who’s the first person you’re going to send it to?',
  shareLead: 'Your message and invite link. Tap it to copy it all.',
  copied: 'Copied. Paste it into a text or an email.',
  linkCopied: 'Your link is copied.',
  // A copy the browser refused (some in-app browsers do): point to a button that needs no copying. Text is on phones only.
  copyFailed: 'That didn’t copy. Tap Text or Email to send it instead.',
  copyFailedComputer: 'That didn’t copy. Tap Email to send it instead.',
  another: 'Try another message',
  // 6. After a share (Jared: "instant gratification")
  sentTitle: 'Awesome job!',
  sentLine: 'That’s one person you just taught about microplastics. Who’s next?',
  sentCountOne: '1 share so far.',
  sentCountMany: ' shares so far.',
  sentFriends: 'A friend counts only once they’ve confirmed their email.',
  done: 'Done for now',
  // Other states
  loading: 'One moment…',
  lockedTitle: 'Your invite link is paused',
  locked: 'Your page says why, and what to do next.',
  noLinkTitle: 'You’re in!',
  noLink: 'Your invite link is waiting on your page.',
  myPage: 'Go to my page',
  closedTitle: 'Invitations are closed',
  closed: 'Thank you for sharing Aelo’s research.',
  close: 'Close',
  newTab: '(opens in a new tab)',
  // Deploy previews only (never the live site): one short line, so the first screen still fits a phone.
  previewNote: 'Preview only. Nothing is sent.',
  previewCode: 'Preview: any 6 digits work.',
} as const

/**
 * How the site's sign-in line for an address it doesn't know ends (linkOutcomeCopy.ts). The pop-up swaps it for
 * COPY.signInUnknown, since its join button sits below; the tests check the two files still agree.
 */
export const FORM_ABOVE = 'Not joined yet? Use the form above.'

/**
 * The buttons (Jared, Oct 18, the share card's layout, for this pop-up too): Text | Email, Facebook | More, then a
 * full-width Copy my link. On a computer Email, Facebook, WhatsApp; inside Facebook's or Instagram's own browser the share
 * card's rules (shareCard.ts mainChannels). "More" opens the share card's own panel of every other app, never the phone's
 * share sheet. Labels as on the share card.
 */
export const CHANNEL_LABELS = {
  sms: 'Text',
  email: 'Email',
  facebook: 'Facebook',
  whatsapp: 'WhatsApp',
  more: 'More',
  copy: 'Copy my link',
} as const
export type MainChannel = 'sms' | 'email' | 'facebook' | 'whatsapp'
/** What a share is recorded as: an app (the share card's names), Copy my link, or a tap on the message itself. */
export type ShareChannel = string

/** The share card's message for each article that this pop-up's own line stands in for (the same line, or the same finding in
 * other words), so "Try another message" never offers it twice. */
export const OWN_LINE_REPLACES: Readonly<Record<string, string>> = {
  'microplastics-in-arteries-study': 'heart-b',
  'microplastics-brain-memory-dementia': 'brain-a',
  'microplastics-fertility-pregnancy-baby-bottles': 'fertility-a',
  'microplastics-weight-gain-mice-studies': 'weight-a',
  'do-animal-studies-matter-for-humans': 'animal-a',
  'microplastics-blood-clots-stroke': 'clots-a',
  'microplastics-children-memory-behavior': 'kids-a',
  'reverse-osmosis-microplastics': 'ro-a',
}
export type ShareText = { id: string; text: string }
/**
 * The messages "Try another message" goes through: the article's own line first (unchanged), then the share card's other
 * messages for it. The own line keeps the card's id when it is that exact line, else "<topic>-popup".
 */
export function articleTexts(slug: string, headline: string, cardTexts: readonly ShareText[]): ShareText[] {
  const text = articleLine(slug, headline)
  const replaces = Object.prototype.hasOwnProperty.call(OWN_LINE_REPLACES, slug) ? OWN_LINE_REPLACES[slug] : null
  const same = cardTexts.find(entry => entry.text === text)
  const topic = (cardTexts[0]?.id ?? 'page-a').replace(/-[^-]*$/, '')
  const own = { id: same?.id ?? `${topic}-popup`, text }
  return [own, ...cardTexts.filter(entry => entry.id !== own.id && entry.id !== replaces && entry.text !== text)]
}

/**
 * The ladder (owner-decisions.md, 2026-10-16): 5, 10, 25 and 50 friends, stacking. No 1,000-friend tier and no signed
 * card any more: never write either. Jared, 2026-10-18: each card, $10 included, says "Total gift value: $X", so nobody
 * reads the tumbler as worth $225 on its own; 50 is "A free exclusive tumbler" (Jared, Oct 17); the
 * gold and silver race is left off this pop-up ("a little clunky"). The photos are the new homepage's product shots
 * (Nichole's design), copied into public/launch/research/assets/gifts/ so this works before that homepage ships.
 */
export type Rung = { count: number; title: string; note: string | null; value: string | null; image: string | null; alt: string; short: string }
export const LADDER: ReadonlyArray<Rung> = [
  { count: 5, title: '$10 Aelo Cash', note: 'Credited to your account', value: 'Total gift value: $10', image: null, alt: '', short: '$10 Aelo Cash' },
  // The filters photo is cropped to the tray (filters-tray.webp): the full shot ends at the box's top-right corner, which read
  // as cut off in this card (Jared, Oct 17).
  { count: 10, title: '3 months of free filters', note: null, value: 'Total gift value: $45', image: '/launch/research/assets/gifts/filters-tray.webp', alt: 'Three Aelo replacement filters in their tray.', short: '3 months of filters' },
  { count: 25, title: 'A free Aelo Straw Kit', note: null, value: 'Total gift value: $125', image: '/launch/research/assets/gifts/kit.webp', alt: 'The black Aelo Straw Kit in its box.', short: 'Aelo Straw Kit' },
  // Jared, Oct 17, word for word. "Exclusive" holds only while this tumbler is never sold or handed out another way.
  { count: 50, title: 'A free exclusive tumbler', note: 'Only for people who bring in 50 friends', value: 'Total gift value: $225', image: '/launch/research/assets/gifts/tumbler.webp', alt: 'The black Aelo tumbler.', short: 'A tumbler you can’t buy' },
]

/** The message for an article: its own line, or (any article added later without one) its headline. */
export function articleLine(slug: string, headline: string): string {
  if (Object.prototype.hasOwnProperty.call(ARTICLE_MESSAGES, slug)) return ARTICLE_MESSAGES[slug]
  const clean = asciiText(headline).replace(/\s+/g, ' ').trim()
  return clean ? (/[.?!]$/.test(clean) ? clean : `${clean}.`) : 'Thought you should see this.'
}

/** Curly quotes, dashes and ellipses as plain ASCII; anything else outside printable ASCII is dropped. */
export function asciiText(text: string): string {
  return text.replace(/[‘’‛′]/g, "'").replace(/[“”‟″]/g, '"').replace(/[–—]/g, '-')
    .replace(/…/g, '...').replace(/ /g, ' ').replace(/[^\x20-\x7E]/g, '')
}

/** The ?ref= code inside the member's invite link from the server, or null. */
export function codeFromReferralUrl(referralUrl: string | null | undefined): string | null {
  if (!referralUrl) return null
  try {
    const url = new URL(referralUrl, SITE)
    if (url.origin !== SITE) return null
    const code = (url.searchParams.get('ref') ?? '').trim().toUpperCase()
    return CODE.test(code) ? code : null
  } catch { return null }
}

/** The article with the member's code on the end: what the friend opens. Null for a bad slug or code. */
export function articleInviteLink(slug: string, referralUrl: string | null | undefined): string | null {
  const code = codeFromReferralUrl(referralUrl)
  if (!code || !SLUG.test(slug)) return null
  return `${SITE}/research/${slug}?ref=${code}`
}

/** A stored group stays; anything else gets a fresh 50/50 pick. */
export function chooseVariant(stored: string | null | undefined, random: number): RowVariant {
  if (stored === 'a' || stored === 'b') return stored
  return random < 0.5 ? 'a' : 'b'
}

/** "1 share so far." / "3 shares so far." */
export function sentCountText(count: number): string {
  const n = Math.max(1, Math.floor(Number.isFinite(count) ? count : 1))
  return n === 1 ? COPY.sentCountOne : `${n.toLocaleString('en-US')}${COPY.sentCountMany}`
}

/**
 * Whether invitations still count, from the public campaign config: only while signups are open and before the
 * cutoff. Null while nothing is known yet (the row stays up; the pop-up checks again before anything is sent).
 */
export function invitationsOpen(
  campaign: { active?: boolean; lifecycleStatus?: string | null; releaseReady?: boolean; submissionsCutoff?: string | null } | null,
  now: number,
): boolean | null {
  if (!campaign) return null
  if (campaign.active !== true || campaign.lifecycleStatus !== 'open' || campaign.releaseReady !== true) return false
  const cutoff = Date.parse(campaign.submissionsCutoff ?? '')
  return !Number.isFinite(cutoff) || now < cutoff
}

/** The live site's own hosts. Anywhere else (a deploy preview, this Mac) there is no sign-up service behind the page. */
export function isLiveHost(hostname: string): boolean {
  return ['aelofilter.com', 'www.aelofilter.com', 'join.aelofilter.com', 'aelo-join.netlify.app'].includes(hostname)
}
